import { getShiftStatus } from '../../shiftUtils';
import { SHIFT_STATUS } from '../../constants/shiftConstants';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';

/**
 * Determines the current shift status (in progress, late, scheduled) and the relevant shift
 * @param {Array} shifts - Array of shift objects
 * @returns {Object} Object containing status and relevantShift
 */
export const getStatusFromWeeklyShifts = (shifts) => {
  if (!shifts?.length) return null;

  // Sort shifts by start time to optimize search
  const sortedShifts = [...shifts].sort((a, b) => new Date(a.start) - new Date(b.start));

  // Find the first shift that matches our priority order
  const activeShift = sortedShifts.find((shift) => {
    const status = getShiftStatus(shift);
    return status === SHIFT_STATUS.IN_PROGRESS || status === SHIFT_STATUS.ON_BREAK || status === SHIFT_STATUS.ON_REST;
  });
  if (activeShift) {
    const actualStatus = getShiftStatus(activeShift);
    return { status: actualStatus, relevantShift: activeShift };
  }

  const lateShift = sortedShifts.find((shift) => getShiftStatus(shift) === SHIFT_STATUS.LATE);
  if (lateShift) {
    return { status: SHIFT_STATUS.LATE, relevantShift: lateShift };
  }

  const upcomingShift = sortedShifts.find((shift) => getShiftStatus(shift) === SHIFT_STATUS.SCHEDULED);
  if (upcomingShift) {
    return { status: SHIFT_STATUS.SCHEDULED, relevantShift: upcomingShift };
  }

  return null;
};

/**
 * Calls backend function to generate shift status message with break deadlines
 * @param {string} employeeId - ID of the employee
 * @returns {Promise<{message: string, status: string, shiftId: string}>} Backend response
 */
export const getEmployeeShiftStatusMessage = async (employeeId) => {
  try {
    const generateStatusMessage = httpsCallable(functions, 'getEmployeeShiftStatusMessage');
    const result = await generateStatusMessage({ employeeId });
    return result.data;
  } catch (error) {
    console.error('Error calling backend shift status:', error);
    if (error.code === 'functions/cors-error' || error.code === 'functions/internal') {
      throw new Error('Unable to connect to server. Please try again later.');
    }
    throw error;
  }
};

/**
 * Determines the actual or scheduled break times based on whether the shift is in the past
 * @param {Object} breakItem - The break object containing scheduled and actual times
 * @param {boolean} isPastShift - Whether the shift is in the past
 * @returns {Object} Object containing start time, end time, and whether they are actual times
 */
export const getBreakTimes = (breakItem, isPastShift) => {
  // For past shifts, prefer actual times over scheduled times
  if (isPastShift) {
    if (breakItem.start && breakItem.end) {
      return {
        startTime: breakItem.start,
        endTime: breakItem.end,
        isActualTime: true,
      };
    }
    // If actual times are not available for a past shift, use scheduled times and mark as missed
    return {
      startTime: breakItem.scheduledStart,
      endTime: breakItem.scheduledEnd,
      isActualTime: false,
    };
  }
  // For future shifts, always use scheduled times
  return {
    startTime: breakItem.scheduledStart,
    endTime: breakItem.scheduledEnd,
    isActualTime: false,
  };
};

/**
 * Determines if a shift is in the past
 * @param {Object} shift - The shift object containing end time
 * @returns {boolean} - Whether the shift is in the past
 */
export const isShiftInPast = (shift) => {
  return new Date(shift.end) < new Date();
};

/**
 * Sorts breaks chronologically based on their start times
 * @param {Array} breaks - Array of break objects
 * @param {boolean} isPastShift - Whether the shift is in the past
 * @returns {Array} Sorted array of breaks
 */
export const sortBreaks = (breaks, isPastShift) => {
  return [...breaks].sort((a, b) => {
    // For past shifts, prefer actual start times
    const aTime = isPastShift && a.start ? a.start : a.scheduledStart;
    const bTime = isPastShift && b.start ? b.start : b.scheduledStart;

    return new Date(aTime) - new Date(bTime);
  });
};
